import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs";
import { environment } from "../../environments/environment";
import { isNativePlatform } from "./platform.service";
import {
  Customer,
  CustomerSummary,
  CustomerInput,
  Estimate,
  EstimateVersion,
  Measurement,
  MeasurementInput,
  Room,
  RoomInput,
  WindowEntity,
  WindowInput,
  Settings,
  BlindCategory,
  CatalogBlind,
  PresignResult,
  Order,
  OrderItem,
  OrderStatus,
  DraftSummary,
  DraftDetail,
} from "./models";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private readonly apiBase = this.resolveApiBase();

  constructor(private readonly http: HttpClient) {}

  private resolveApiBase(): string {
    if (typeof window === "undefined") {
      if (
        typeof environment.apiBaseUrl === "string" &&
        environment.apiBaseUrl.trim().length > 0
      ) {
        return environment.apiBaseUrl.replace(/\/+$/, "");
      }
      return "";
    }

    const globalApiBase = (window as unknown as { __API_BASE_URL__?: string })
      .__API_BASE_URL__;
    if (typeof globalApiBase === "string" && globalApiBase.trim().length > 0) {
      return globalApiBase.replace(/\/+$/, "");
    }

    // Native (Capacitor) shell: WKWebView reports localhost, but there is no
    // local API server on the device. Always use the real base from the
    // environment file (dev/prod baked at build time). Web keeps the
    // localhost:4000 dev shortcut below.
    // Uses the standalone fn (not this.platform) because apiBase is a field
    // initializer that runs before the constructor assigns the injectable.
    if (isNativePlatform()) {
      const baked = (environment.apiBaseUrl || "").trim().replace(/\/+$/, "");
      if (baked) return baked;
    }

    const hostname = window.location?.hostname ?? "";
    const protocol = window.location?.protocol ?? "";
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      protocol === "file:"
    ) {
      return "http://localhost:4000";
    }

    if (
      typeof environment.apiBaseUrl === "string" &&
      environment.apiBaseUrl.trim().length > 0
    ) {
      return environment.apiBaseUrl.replace(/\/+$/, "");
    }

    const meta = document.querySelector('meta[name="api-base"]');
    const metaValue = meta?.getAttribute("content");
    if (typeof metaValue === "string" && metaValue.trim().length > 0) {
      return metaValue.replace(/\/+$/, "");
    }

    return "";
  }

  getCustomers() {
    return this.http.get<Customer[]>(`${this.apiBase}/customers`);
  }

  getSettings() {
    return this.http.get<Settings>(`${this.apiBase}/settings`);
  }

  updateSettings(payload: Partial<Settings>) {
    return this.http.put<Settings>(`${this.apiBase}/settings`, payload);
  }

  getCustomerSummaries() {
    return this.http
      .get<CustomerSummary[]>(`${this.apiBase}/customers/summary`)
      .pipe(map((data) => data || []));
  }

  getCustomer(customerId: string) {
    return this.http.get<Customer>(`${this.apiBase}/customers/${customerId}`);
  }

  createCustomer(payload: CustomerInput) {
    return this.http.post<Customer>(`${this.apiBase}/customers`, payload);
  }

  createRemake(
    customerId: string,
    windows: { windowId: string; width: number; height: number }[],
  ) {
    return this.http.post<Customer>(
      `${this.apiBase}/customers/${customerId}/remake`,
      { windows },
    );
  }

  /** Create a Stripe payment link for an Own-Job to send the customer. */
  createJobPaymentLink(customerId: string, amount: number) {
    return this.http.post<{ url: string }>(
      `${this.apiBase}/checkout/customers/${customerId}/payment-link`,
      { amount },
    );
  }

  updateCustomer(customerId: string, payload: Partial<CustomerInput>) {
    return this.http.patch<Customer>(
      `${this.apiBase}/customers/${customerId}`,
      payload,
    );
  }

  deleteCustomer(customerId: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/customers/${customerId}`,
    );
  }

  archiveCustomer(customerId: string) {
    return this.http.post<Customer>(
      `${this.apiBase}/customers/${customerId}/archive`,
      {},
    );
  }

  restoreCustomer(customerId: string) {
    return this.http.post<Customer>(
      `${this.apiBase}/customers/${customerId}/restore`,
      {},
    );
  }

  getRooms(customerId: string) {
    return this.http.get<Room[]>(
      `${this.apiBase}/customers/${customerId}/rooms`,
    );
  }

  getRoom(customerId: string, roomId: string) {
    return this.http.get<Room>(
      `${this.apiBase}/customers/${customerId}/rooms/${roomId}`,
    );
  }

  createRoom(customerId: string, payload: RoomInput) {
    return this.http.post<Room>(
      `${this.apiBase}/customers/${customerId}/rooms`,
      payload,
    );
  }

  updateRoom(customerId: string, roomId: string, payload: Partial<RoomInput>) {
    return this.http.patch<Room>(
      `${this.apiBase}/customers/${customerId}/rooms/${roomId}`,
      payload,
    );
  }

  deleteRoom(customerId: string, roomId: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/customers/${customerId}/rooms/${roomId}`,
    );
  }

  getWindows(roomId: string) {
    return this.http.get<WindowEntity[]>(
      `${this.apiBase}/rooms/${roomId}/windows`,
    );
  }

  /** Returns all windows across every room of a customer in one request. */
  getCustomerWindows(customerId: string) {
    return this.http.get<WindowEntity[]>(
      `${this.apiBase}/customers/${customerId}/windows`,
    );
  }

  getWindow(roomId: string, windowId: string) {
    return this.http.get<WindowEntity>(
      `${this.apiBase}/rooms/${roomId}/windows/${windowId}`,
    );
  }

  createWindow(roomId: string, payload: WindowInput) {
    return this.http.post<WindowEntity>(
      `${this.apiBase}/rooms/${roomId}/windows`,
      payload,
    );
  }

  updateWindow(
    roomId: string,
    windowId: string,
    payload: Partial<WindowInput>,
  ) {
    return this.http.patch<WindowEntity>(
      `${this.apiBase}/rooms/${roomId}/windows/${windowId}`,
      payload,
    );
  }

  deleteWindow(roomId: string, windowId: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/rooms/${roomId}/windows/${windowId}`,
    );
  }

  getMeasurements(windowId: string) {
    return this.http.get<Measurement[]>(
      `${this.apiBase}/windows/${windowId}/measurements`,
    );
  }

  getMeasurementsBulk(windowIds: string[]) {
    return this.http.post<Measurement[]>(`${this.apiBase}/measurements/bulk`, {
      windowIds,
    });
  }

  getMeasurement(windowId: string, measurementId: string) {
    return this.getMeasurements(windowId).pipe(
      map(
        (measurements) =>
          measurements.find((m) => m.id === measurementId) ?? null,
      ),
      map((measurement) => {
        if (!measurement) {
          throw new Error("Measurement not found");
        }
        return measurement;
      }),
    );
  }

  createMeasurement(windowId: string, payload: MeasurementInput) {
    return this.http.post<Measurement>(
      `${this.apiBase}/windows/${windowId}/measurements`,
      payload,
    );
  }

  updateMeasurement(
    windowId: string,
    measurementId: string,
    payload: Partial<MeasurementInput>,
  ) {
    return this.http.patch<Measurement>(
      `${this.apiBase}/windows/${windowId}/measurements/${measurementId}`,
      payload,
    );
  }

  deleteMeasurement(windowId: string, measurementId: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/windows/${windowId}/measurements/${measurementId}`,
    );
  }

  getEstimate(customerId: string) {
    return this.http.get<Estimate>(
      `${this.apiBase}/customers/${customerId}/estimate`,
    );
  }

  getEstimateVersions(customerId: string) {
    return this.http.get<EstimateVersion[]>(
      `${this.apiBase}/customers/${customerId}/estimate/versions`,
    );
  }

  getEstimateVersion(customerId: string, version: number) {
    return this.http.get<Estimate>(
      `${this.apiBase}/customers/${customerId}/estimate/versions/${version}`,
    );
  }

  saveEstimate(customerId: string, estimate: Estimate) {
    return this.http.post<Estimate>(
      `${this.apiBase}/customers/${customerId}/estimate`,
      estimate,
    );
  }

  getCategories() {
    return this.http.get<BlindCategory[]>(`${this.apiBase}/catalog/categories`);
  }

  createCategory(name: string) {
    return this.http.post<BlindCategory>(`${this.apiBase}/catalog/categories`, {
      name,
    });
  }

  updateCategory(
    id: string,
    payload: { name?: string; imageKey?: string; imageUrl?: string },
  ) {
    return this.http.patch<BlindCategory>(
      `${this.apiBase}/catalog/categories/${id}`,
      payload,
    );
  }

  deleteCategory(id: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/catalog/categories/${id}`,
    );
  }

  getCatalogBlinds() {
    return this.http.get<CatalogBlind[]>(`${this.apiBase}/catalog/blinds`);
  }

  getCatalogBlind(id: string) {
    return this.http.get<CatalogBlind>(`${this.apiBase}/catalog/blinds/${id}`);
  }

  createBlind(input: Partial<CatalogBlind>) {
    return this.http.post<CatalogBlind>(`${this.apiBase}/catalog/blinds`, input);
  }

  updateBlind(id: string, input: Partial<CatalogBlind>) {
    return this.http.patch<CatalogBlind>(
      `${this.apiBase}/catalog/blinds/${id}`,
      input,
    );
  }

  deleteBlind(id: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/catalog/blinds/${id}`,
    );
  }

  presignCatalogImage(contentType: string, filename: string) {
    return this.http.post<PresignResult>(`${this.apiBase}/catalog/images/presign`, {
      contentType,
      filename,
    });
  }

  generateGallery(blindId: string) {
    return this.http.post<{ status: string }>(
      `${this.apiBase}/catalog/blinds/${blindId}/gallery/generate`,
      {},
    );
  }

  galleryStatus(blindId: string) {
    return this.http.get<{
      status: string;
      images?: { url: string; scene: string }[];
      error?: string;
    }>(`${this.apiBase}/catalog/blinds/${blindId}/gallery`);
  }

  regenerateGallery(blindId: string, index: number) {
    return this.http.post<{ status: string }>(
      `${this.apiBase}/catalog/blinds/${blindId}/gallery/regenerate`,
      { index },
    );
  }

  saveGallery(blindId: string) {
    return this.http.post<CatalogBlind>(
      `${this.apiBase}/catalog/blinds/${blindId}/gallery/save`,
      {},
    );
  }

  cancelGallery(blindId: string) {
    return this.http.delete<void>(
      `${this.apiBase}/catalog/blinds/${blindId}/gallery`,
    );
  }

  getOrders() {
    return this.http.get<Order[]>(`${this.apiBase}/orders`).pipe(map((data) => data || []));
  }

  getOrder(id: string) {
    return this.http.get<Order>(`${this.apiBase}/orders/${id}`);
  }

  updateOrder(id: string, patch: Record<string, unknown>) {
    return this.http.patch<Order>(`${this.apiBase}/orders/${id}`, patch);
  }

  /** Live customer carts (guest + signed-in), read-only. */
  getDrafts() {
    return this.http
      .get<DraftSummary[]>(`${this.apiBase}/draft/admin`)
      .pipe(map((data) => data || []));
  }

  getDraftAdmin(ownerId: string) {
    return this.http.get<DraftDetail>(
      `${this.apiBase}/draft/admin/${encodeURIComponent(ownerId)}`,
    );
  }

  updateOrderStatus(id: string, status: OrderStatus) {
    return this.http.patch<Order>(`${this.apiBase}/orders/${id}/status`, { status });
  }

  updateOrderTracking(id: string, trackingNumber: string) {
    return this.http.patch<Order>(`${this.apiBase}/orders/${id}/tracking`, {
      trackingNumber,
    });
  }

  markOrderPaid(id: string) {
    return this.http.patch<Order>(`${this.apiBase}/orders/${id}/payment`, {});
  }

  approveDiyQuote(orderId: string, supplierId: string) {
    return this.http.post<void>(
      `${this.apiBase}/orders/${orderId}/supplier-quote/${supplierId}/approve`,
      {},
    );
  }

  rejectDiyQuote(orderId: string, supplierId: string) {
    return this.http.post<void>(
      `${this.apiBase}/orders/${orderId}/supplier-quote/${supplierId}/reject`,
      {},
    );
  }

  markDiyPaid(orderId: string, supplierId: string) {
    return this.http.post<void>(
      `${this.apiBase}/orders/${orderId}/supplier-quote/${supplierId}/paid`,
      {},
    );
  }

  listSuppliers() {
    return this.http.get<{
      id: string;
      name: string;
      email: string;
      createdAt: string;
    }[]>(`${this.apiBase}/suppliers`);
  }

  createSupplier(input: { name: string; email: string }) {
    return this.http.post<{
      id: string;
      name: string;
      email: string;
      createdAt: string;
    }>(`${this.apiBase}/suppliers`, input);
  }

  deleteSupplier(id: string) {
    return this.http.delete<{ deleted: boolean }>(
      `${this.apiBase}/suppliers/${id}`,
    );
  }

  assignSupplier(customerId: string, supplierId: string) {
    return this.updateCustomer(customerId, { supplierId });
  }

  approveSupplierQuote(customerId: string, supplierId?: string) {
    const path = supplierId
      ? `${this.apiBase}/customers/${customerId}/supplier-quote/${supplierId}/approve`
      : `${this.apiBase}/customers/${customerId}/supplier-quote/approve`;
    return this.http.post<void>(path, {});
  }

  rejectSupplierQuote(customerId: string, supplierId?: string) {
    const path = supplierId
      ? `${this.apiBase}/customers/${customerId}/supplier-quote/${supplierId}/reject`
      : `${this.apiBase}/customers/${customerId}/supplier-quote/reject`;
    return this.http.post<void>(path, {});
  }

  markJobSupplierPaid(customerId: string, supplierId: string) {
    return this.http.post<void>(
      `${this.apiBase}/customers/${customerId}/supplier-quote/${supplierId}/paid`,
      {},
    );
  }

  /** Approve the job: route windows to suppliers and notify them. */
  approveOrder(customerId: string) {
    return this.http.post<{ supplierIds: string[] }>(
      `${this.apiBase}/customers/${customerId}/approve-order`,
      {},
    );
  }

  // ── Email templates ────────────────────────────────────────────────────────
  listEmailTemplates() {
    return this.http.get<
      { name: string; overridden: boolean; updatedAt?: string }[]
    >(`${this.apiBase}/emails/templates`);
  }

  getEmailTemplate(name: string) {
    return this.http.get<{
      name: string;
      source: string;
      overridden: boolean;
      updatedAt?: string;
    }>(`${this.apiBase}/emails/templates/${encodeURIComponent(name)}`);
  }

  saveEmailTemplate(name: string, source: string) {
    return this.http.put<{
      name: string;
      source: string;
      overridden: boolean;
      updatedAt?: string;
    }>(`${this.apiBase}/emails/templates/${encodeURIComponent(name)}`, {
      source,
    });
  }

  resetEmailTemplate(name: string) {
    return this.http.post<{
      name: string;
      source: string;
      overridden: boolean;
      updatedAt?: string;
    }>(
      `${this.apiBase}/emails/templates/${encodeURIComponent(name)}/reset`,
      {},
    );
  }

  /** Renders unsaved template source → returns raw HTML (for live preview). */
  previewEmailSource(name: string, source: string) {
    return this.http.post(
      `${this.apiBase}/emails/preview`,
      { name, source },
      { responseType: "text" },
    );
  }
}
